import { useState, useCallback, useRef } from 'react'
import { Upload, X, Image as ImageIcon, User } from 'lucide-react'
import { Button, Label, Input } from '@creanizer/ui'
import { cn } from '@creanizer/utils'

interface ImageUploaderProps {
  type: 'avatar' | 'header'
  documentType: string
  avatars?: { [key: string]: string }
  headerImage?: string
  onAvatarUpload?: (name: string, image: string) => void
  onHeaderUpload?: (image: string) => void
  onAvatarRemove?: (name: string) => void
  onHeaderRemove?: () => void
}

export function ImageUploader({
  type,
  documentType,
  avatars = {},
  headerImage,
  onAvatarUpload,
  onHeaderUpload,
  onAvatarRemove,
  onHeaderRemove
}: ImageUploaderProps) {
  const [avatarName, setAvatarName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const headerFileInputRef = useRef<HTMLInputElement>(null)

  // ファイルをBase64に変換
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = error => reject(error)
    })
  }

  // ファイルアップロード処理
  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const file = files[0]
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください')
      return
    }

    // ファイルサイズ制限（2MB）
    if (file.size > 2 * 1024 * 1024) {
      alert('画像サイズは2MB以下にしてください')
      return
    }

    try {
      const base64 = await fileToBase64(file)
      
      if (type === 'avatar') {
        if (!avatarName.trim()) {
          alert('キャラクター名を入力してください')
          return
        }
        onAvatarUpload?.(avatarName, base64)
        setAvatarName('')
      } else {
        onHeaderUpload?.(base64)
      }
    } catch (error) {
      console.error('画像のアップロードに失敗しました:', error)
      alert('画像のアップロードに失敗しました')
    }
  }, [type, avatarName, onAvatarUpload, onHeaderUpload])

  // ドラッグ&ドロップ処理
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileUpload(e.dataTransfer.files)
  }, [handleFileUpload])

  // 対応する文献タイプかチェック
  const isSupportedType = () => {
    if (type === 'avatar') {
      return ['sns_twitter', 'line', 'blog', 'email', 'letter'].includes(documentType)
    } else {
      return ['blog', 'newspaper', 'magazine'].includes(documentType)
    }
  }

  if (!isSupportedType()) {
    return null
  }

  return (
    <div className="space-y-4">
      <Label>
        {type === 'avatar' ? 'キャラクター画像' : 'ヘッダー画像'}
      </Label>

      {type === 'avatar' && (
        <div className="space-y-4">
          {/* キャラクター名入力 */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              キャラクターの正式名を入力してください（AIが自動的に適切なユーザー名に変換します）
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="キャラクター名（例: 山田太郎）"
                value={avatarName}
                onChange={(e) => setAvatarName(e.target.value)}
                className="flex-1"
              />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
              disabled={!avatarName.trim()}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!avatarName.trim()}
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              画像選択
            </Button>
          </div>
        </div>

          {/* アップロード済みアバター一覧 */}
          {Object.keys(avatars).length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">登録済みキャラクター:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(avatars).map(([name, image]) => (
                  <div
                    key={name}
                    className="relative group border rounded-lg p-2 bg-white"
                  >
                    <img
                      src={image}
                      alt={name}
                      className="w-full h-20 object-cover rounded"
                    />
                    <p className="text-xs mt-1 truncate">{name}</p>
                    <button
                      onClick={() => onAvatarRemove?.(name)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {type === 'header' && (
        <div className="space-y-4">
          {/* ヘッダー画像アップロード */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300",
              headerImage && "p-0"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {headerImage ? (
              <div className="relative group">
                <img
                  src={headerImage}
                  alt="ヘッダー画像"
                  className="w-full h-48 object-cover rounded"
                />
                <button
                  onClick={onHeaderRemove}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <input
                  ref={headerFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                />
                <div 
                  className="cursor-pointer block"
                  onClick={() => headerFileInputRef.current?.click()}
                >
                  <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600">
                    画像をドラッグ&ドロップ
                    <br />
                    または
                    <span className="text-blue-600 hover:underline ml-1">
                      クリックして選択
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    対応形式: JPG, PNG, GIF (最大2MB)
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}